import { useState, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { itemsData } from "../../data/itemsData";
import Slider from "react-slick";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Footer from "../../components/Footer/Footer";
import Navbar from "../Navbar/Navabar";
import "./SubcategoryPage.css";

function SubcategoryPage() {
  const { id, itemId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedItem = location.state || null;
  const [currentImage, setCurrentImage] = useState(0);

  const categoryItems = itemsData[id] || [];
  const item = categoryItems.find(
    (product) => product.id.toString() === itemId
  );

  const similarProducts = categoryItems.filter(
    (product) => product.id !== item?.id
  );

  const productImages = Array.isArray(item?.images)
    ? item.images
    : item?.img
      ? [item.img]
      : [];

  // Organize content into left and right sections
  const contentSections = useMemo(() => {
    if (!item) return { left: [], right: [] };

    const leftSection = [
      { key: 'composition', title: 'COMPOSITION', content: item.composition },
      { key: 'description', title: 'DESCRIPTION', content: item.description },
      { key: 'KeyEnzymes', title: 'KEY ENZYMES', content: item.KeyEnzymes },
      { key: 'mechanismOfAction', title: 'MECHANISM OF ACTION', content: item.mechanismOfAction },
      { key: 'modeOfAction', title: 'MODE OF ACTION', content: item.modeOfAction },
    ].filter(section => section.content);

    const rightSection = [
      { key: 'features', title: 'FEATURES', content: item.features },
      { key: 'benefits', title: 'BENEFITS', content: item.benefits },
      { key: 'functions', title: 'FUNCTIONS', content: item.functions },
      { key: 'indications', title: 'INDICATIONS', content: item.indications },
      { key: 'dosage', title: 'DOSAGE', content: item.dosage },
    ].filter(section => section.content);

    return { left: leftSection, right: rightSection };
  }, [item]);

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" }
    },
    exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2 } }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.4,
        when: "beforeChildren",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  const PrevArrow = (props) => (
    <button className="custom-arrow prev" onClick={props.onClick}>
      <ChevronLeft />
    </button>
  );

  const NextArrow = (props) => (
    <button className="custom-arrow next" onClick={props.onClick}>
      <ChevronRight />
    </button>
  );

  const sliderSettings = {
    dots: false,
    infinite: similarProducts.length > 4,
    speed: 500,
    slidesToShow: Math.min(similarProducts.length, 4),
    slidesToScroll: 1,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: Math.min(similarProducts.length, 3),
        },
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: Math.min(similarProducts.length, 2),
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const renderFormattedText = (text) => {
    if (!text) return null;

    return text.split('\n').map((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return null;

      if (trimmedLine.startsWith('-') || trimmedLine.startsWith('•')) {
        return (
          <div key={index} className="bullet-item">
            {trimmedLine.replace(/^[-•]/, "").trim()}
          </div>
        );
      }

      return (
        <div key={index} className="text-line">
          {trimmedLine}
        </div>
      );
    });
  };

  const renderInfoCard = (title, content, className = "") => {
    if (!content) return null;

    return (
      <motion.div className={`info-card ${className}`} variants={itemVariants}>
        <h3 className="info-title">{title}</h3>
        <div className="info-content">
          {typeof content === 'string' ? renderFormattedText(content) : content}
        </div>
      </motion.div>
    );
  };

  if (!item) {
    return (
      <div className="container text-center py-5">
        <h2 className="text-danger">Item not found</h2>
        <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>
          Go Back
        </button>
      </div>
    );
  }

  // Determine layout class based on content availability
  const hasLeftContent = contentSections.left.length > 0;
  const hasRightContent = contentSections.right.length > 0;
  const layoutClass = !hasLeftContent && hasRightContent ? 'layout-center-right' :
                       hasLeftContent && !hasRightContent ? 'layout-left-center' :
                       hasLeftContent && hasRightContent ? 'layout-three-column' :
                       'layout-center-only';

  return (
    <>
      <Navbar />

      <motion.section
        className="product-showcase"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="container-fluid">
          <motion.div className="product-container" variants={itemVariants}>

            <div className="product-header">
              <div className="product-box">
                <h1 className="product-name">{item.name}</h1>
                <div className="product-presentation">
                  <span>{item.subTitle}</span>
                </div>
              </div>
            </div>

            {(item.productOverview || item.ProductOverview) && (
              <motion.div className="full-width-section" variants={itemVariants}>
                {renderInfoCard("PRODUCT OVERVIEW", item.productOverview || item.ProductOverview, "overview-card")}
              </motion.div>
            )}

            <div className={`product-content-grid ${layoutClass}`}>

              {hasLeftContent && (
                <div className="content-column left-column">
                  <motion.div className="info-section" variants={itemVariants}>
                    {contentSections.left.map(section => (
                      <div key={section.key}>
                        {renderInfoCard(section.title, section.content)}
                      </div>
                    ))}
                  </motion.div>
                </div>
              )}

              <div className="content-column center-column">
                <motion.div className="center-image-section" variants={itemVariants}>
                  <div className="main-image-container">
                    <AnimatePresence mode="wait">
                      <motion.img
                        key={currentImage}
                        src={productImages[currentImage] || item.img}
                        alt={item.name}
                        className="main-product-image"
                        variants={imageVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                      />
                    </AnimatePresence>
                  </div>

                  {productImages.length > 1 && (
                    <div className="thumbnail-gallery">
                      {productImages.map((src, idx) => (
                        <motion.div
                          key={idx}
                          className={`thumbnail-wrapper ${idx === currentImage ? 'active' : ''}`}
                          onClick={() => setCurrentImage(idx)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <img
                            src={src}
                            alt={`${item.name} view ${idx + 1}`}
                            className="thumbnail-image"
                          />
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              </div>

              {hasRightContent && (
                <div className="content-column right-column">
                  <motion.div className="info-section" variants={itemVariants}>
                    {contentSections.right.map(section => (
                      <div key={section.key}>
                        {renderInfoCard(section.title, section.content)}
                      </div>
                    ))}
                  </motion.div>
                </div>
              )}
            </div>

            <div className="additional-sections">
              {item.disclaimer && (
                <motion.div className="disclaimer-section" variants={itemVariants}>
                  <div className="disclaimer-card">
                    <h4 className="disclaimer-title">DISCLAIMER</h4>
                    <p className="disclaimer-text">{item.disclaimer}</p>
                  </div>
                </motion.div>
              )}
            </div>

          </motion.div>

          {similarProducts.length > 0 && (
            <motion.div
              className="similar-products-section"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <div className="section-header">
                <h2 className="section-title">You Might Also Like</h2>
                <div className="section-divider" />
              </div>

              <div className="products-slider">
                <Slider {...sliderSettings}>
                  {similarProducts.map((product) => (
                    <div key={product.id} className="product-slide">
                      <motion.div
                        className="product-card"
                        whileHover={{ y: -8, scale: 1.02 }}
                        transition={{ duration: 0.3 }}
                        onClick={() =>
                          navigate(`/subcategory/${id}/${product.id}`, {
                            state: product,
                          })
                        }
                      >
                        <div className="product-image-wrapper">
                          <img
                            src={product.img}
                            alt={product.name}
                            className="product-card-image"
                          />
                          <div className="product-overlay">
                            <span className="view-details">View Details</span>
                          </div>
                        </div>
                        <div className="product-card-content">
                          <h4 className="product-card-title">{product.name}</h4>
                          <p className="product-card-subtitle">{product.subTitle}</p>
                        </div>
                      </motion.div>
                    </div>
                  ))}
                </Slider>
              </div>
            </motion.div>
          )}
        </div>
      </motion.section>

      <Footer />
    </>
  );
}

export default SubcategoryPage;